import React, { useEffect, useMemo, useState } from "react";
import { Row, Col } from "react-bootstrap";
import Axios from "axios";
import DatePicker from "react-datepicker";
import MaterialTable from "material-table";
import moment from "moment-timezone";

import { calculateTargetProd } from "../../../util/targetCalc";
import { useFormatter } from "../../../util/formatter";
import { useSolarCalc } from "../../../util/calculations";
import { useUser } from "../../../util/user";
import GYAxios from "../../../util/GYAxios";
import Noti from "../../../util/noti";
import { localTimeToPickerTime, pickerTimeTolocalTime } from "../../../util/momentTimezone";
import { useUserProps } from "../../../util/userDBContext";
import { isITUser, isManagementUser } from "../../../util/roles";

export const SummaryReportOps = (props) => {
	const user = useUser();
	const { loggedCountry } = useUserProps();
	const defaultCountry = useMemo(() => (isITUser(user.roles) || isManagementUser(user.roles)) ? "ALL" : loggedCountry, [loggedCountry, user.roles]);
	const [siteSet, setSiteSet] = useState(defaultCountry);
	const [formatNumber] = useFormatter();
	const [isReady, setIsReady] = useState(false);
	const [selectedMonth, setSelectedMonth] = useState(
		moment().subtract(1, "month")
	);
	const [yearMonth, setYearMonth] = useState(selectedMonth.format("MMMM YYYY"));
	const [selectedYear, setSelectedYear] = useState(
		selectedMonth.format("YYYY")
	);
	const minDate = moment("2021-01-01", "YYYY-MM-DD").toDate();
	const maxDate = moment().subtract(1, "month").endOf("month").toDate();

	const [isDisabled, setIsDisabled] = useState(null);
	const [summarySites, setSummarySites] = useState([]);
	const [columns, setColumns] = useState([]);
	const [rows, setRows] = useState([]);
	const solarCalc = useSolarCalc();

	/**
	 * Fetch data for summary report of site groups(MK1, MK2, CP)
	 * @param {Site group to fetch} siteSet
	 */
	const fetchData = (siteSet) => {
		let clientID = "";
		switch (siteSet) {
			case "MK1":
			case "MK2":
				clientID = 18;
				break;
			case "CP":
				clientID = 21;
				break;

			default:
				break;
		}

		const endOfMonth = selectedMonth.endOf("month");
		const toDateParam = endOfMonth.format("YYYYMMDD");

		const yearMonth = selectedMonth.startOf("month").format("YYYYMM");

		let column = "admin";
		let id = "all";

		const viewLevel = props.viewLevel;
		let reqMonthlyURI = ``;
		let filterStr = "";
		// Client selected
		if (clientID != "") {
			column = "client_id";
			id = clientID;
			reqMonthlyURI = `/reports/summary/ops/filter/${column}/${id}/month/${yearMonth}/level/${viewLevel}`;
			filterStr = `filter?column=client_id&value=${id}`;
		} else {
			// Country selected
			const countryCol = "country";
			const country = siteSet;
			reqMonthlyURI = `/reports/summary/ops/filter/${countryCol}/${country}/month/${yearMonth}/level/${viewLevel}`;
			filterStr = `filter?column=country&value=${country}`;
		}

		const codURI = `/site/cod/moment/${toDateParam}/filter/${column}/${id}`;
		const reqCOD = GYAxios.get(codURI);
		const reqEnergy = GYAxios.get(reqMonthlyURI);

		const paramReq = GYAxios.get(`/target/parameters/${filterStr}`);
		const unusedReq = GYAxios.get(`/target/unused/${filterStr}`);
		const egridReq = GYAxios.get(`/target/egrid/${filterStr}`);
		const irrReq = GYAxios.get(`/target/irradiance/${filterStr}`);
		const tempReq = GYAxios.get(`/target/temperature/${filterStr}`);

		const reqFreeSites = GYAxios.get("/site/free");

		const reqArray = [
			reqCOD,
			reqEnergy,
			paramReq,
			unusedReq,
			egridReq,
			irrReq,
			tempReq,
			reqFreeSites,
		];

		Axios.all(reqArray)
			.then(
				Axios.spread((...responses) => {
					// API responses
					const resCOD = responses[0].data;
					const tempResEnergy = responses[1].data;
					const resParam = responses[2].data;
					const resUnused = responses[3].data;
					const resEgrid = responses[4].data;
					const resIrr = responses[5].data;
					const resTemp = responses[6].data;
					const resFreeSites = responses[7];
					const resEnergy = tempResEnergy.map((energyObj) =>
						formatResEnergy(
							energyObj,
							resParam,
							resUnused,
							resEgrid,
							resIrr,
							resTemp,
							selectedMonth
						)
					).filter(Boolean);

					let summarySites = [];

					const freeSites =
						resFreeSites.data.error == null
							? resFreeSites.data.map((freeSite) => freeSite.table_name)
							: [];

					// Client selected
					if (clientID != "") {
						resCOD.map((CODsite, id) => {
							for (let i = 0; i < resEnergy.length; i++) {
								const site = resEnergy[i];

								// Site is COD and not a free site
								if (
									CODsite.table_name === site.tableName &&
									!freeSites.includes(site.tableName)
								) {
									// Site is a Makro Site
									if (siteSet.includes("MK")) {
										// Site is a MK Phase 1 site
										if (siteSet === "MK1" && site.siteCode.length == 5)
											summarySites.push({ ...site, index: id });
										// Site is a MK Phase 2 site
										else if (siteSet === "MK2" && site.siteCode.length == 6)
											summarySites.push({ ...site, index: id });
									}
									// Site is a CP ALL site
									else summarySites.push({ ...site, index: id });

									break;
								}
							}
						});
					}
					// Country selected
					else {
						resCOD.map((CODsite, id) => {
							for (let i = 0; i < resEnergy.length; i++) {
								const site = resEnergy[i];

								// Site is COD
								if (CODsite.table_name === site.tableName) {
									summarySites.push({ ...site, index: id });

									break;
								}
							}
						});
					}
					setSummarySites(summarySites);
					setIsReady(true);
				})
			)
			.catch((err) => console.log("Request error: ", err));
	};

	/**
	 * Format res energy object
	 * @param {obj} energyObj
	 * @param {obj} resExp
	 * @param {obj} resIrr
	 * @param {obj} resTemp
	 * @returns Formatted res energy object
	 */
	const formatResEnergy = (
		energyObj,
		resParam,
		resUnused,
		resEgrid,
		resIrr,
		selectedMonth
	) => {
		// Selected Month and year
		const MMMM = moment(selectedMonth).format("MMMM").toLowerCase();
		const YYYY = moment(selectedMonth).format("YYYY");

		// Site details
		const tableName = energyObj.tableName;
		const capacity = energyObj.capacity;
		const contractDuration = energyObj.contractDuration;
		const CODobj = moment(energyObj.COD);
		const nowObj = moment();
		const formattedCOD = CODobj.format("YYYY-MM-DD");
		const accumMonths = nowObj.diff(CODobj, "months");

		// Target data
		const filteredParameter = resParam.filter(
			(obj) => obj.table_name === tableName
		);
		const parameterObj = filteredParameter[0] ?? {};

		// Correction percentages
		const GACorrections = parameterObj.GA_correction ?? 0;
		const TACorrections = parameterObj.TA_correction ?? 0;
		const otherCorrections = parameterObj.other_corrections ?? 0;

		const summedCorrections = GACorrections + TACorrections + otherCorrections;

		// Yearly degradation
		const degradation = parameterObj.degradation;

		// Get expected irradiance of selected month
		const filteredIrr = resIrr.filter((obj) => obj.table_name === tableName);
		const irrObj = filteredIrr[0];

		// Format unused data
		const filteredUnused = resUnused.filter(
			(obj) => obj.table_name === tableName
		);
		const unusedObj = filteredUnused[0];

		// EGrid data
		const filteredEgrid = resEgrid.filter(
			(obj) => obj.table_name === tableName
		);
		const egridObj = filteredEgrid[0];
		
		if (
			parameterObj == null ||
			irrObj == null ||
			unusedObj == null ||
			egridObj == null
		) {
			console.warn(`Missing target data for site: ${tableName}`);
			return null;
		}

		// Calculate monthly target production
		const targetProd = calculateTargetProd(
			egridObj,
			capacity,
			summedCorrections,
			unusedObj,
			degradation,
			formattedCOD,
			contractDuration
		);

		// Get BP Target productions
		const BPtargetProd = targetProd[0];
		const allTargetProd = BPtargetProd.flat();

		// Get target production of selected month
		const selectedTarget = allTargetProd.filter(
			(prod) => prod.month == MMMM && prod.year == YYYY
		);

		// Target values
		const targetExp =
			selectedTarget.length > 0 ? selectedTarget[0].monthlyProd : null;
		const targetIrr = irrObj[MMMM] != null ? irrObj[MMMM] : "-";

		// Parameters to calculate indice
		const indiceParam = {
			actualProd: energyObj.actualProd,
			expProd: targetExp,
			actualIrr: solarCalc.calculateIrr(energyObj.actualIrr),
			expIrr: targetIrr,
		};

		// Calculated values
		const coeffOversize = energyObj.coeffOversize;
		const expProd = indiceParam.expProd;
		const coeffExpProd =
			expProd != null ? (coeffOversize / 100) * expProd : null;

		return {
			...energyObj,
			year: nowObj.format("YYYY"),
			COD: formattedCOD,
			coeffExpProd,
			accumMonths,
		};
	};

	/**
	 * Format summary sites array
	 * @param {Summary Sites} summarySites
	 * @returns array - formatted summary sites
	 */
	const formatSummSites = (summarySites) => {
		const width = 150;

		const titles = [
			{ title: "Client", field: "company", width },
			{
				title: "Site Name",
				field: "siteName",
				grouping: false,
				width,
			},
			{
				title: "Site Code",
				field: "siteCode",
				grouping: false,
				width,
			},
			{
				title: "Capacity (kWh)",
				field: "capacity",
				grouping: false,
				width,
			},
			{
				title: "COD Date",
				field: "COD",
				grouping: false,
				width,
			},
			{
				title: "Month of accumulated prod",
				field: "accumMonths",
				grouping: false,
				width,
			},
			{
				title: "Theoretical PR (%)",
				field: "theoreticalPR",
				grouping: false,
				width,
			},
			{
				title: "TA (%)",
				field: "TA",
				grouping: false,
				width,
			},
			{
				title: "Grid Availability (%)",
				field: "gridAv",
				grouping: false,
				width,
			}
		];

		const data = summarySites.map((site) => {
			// const actualIrr = solarCalc.calculateIrr(site.actualIrr);

			return {
				...site,
				theoreticalPR: formatNumber(site.theoreticalPR, 2),
				TA: formatNumber(site.TA, 2),
				gridAv: formatNumber(site.gridAv, 2),
			};
		});

		setColumns(titles);
		setRows(data);
	};

	/**
	 * Show under-review notifications
	 */
	const alertMsg = () => {
		Noti.create({
			type: "w",
			msg: "Invoices of the group are still under review",
		});
	};

	useEffect(() => {
		const param = {
			date: moment(selectedMonth.format("YYYY-MM-DD")).endOf("month"),
			viewStatus: props.viewLevel,
		};
		const MK1SitesReq = GYAxios.post("/reports/summary_groups", {
			...param,
			group: "MK1",
		});
		const MK2SitesReq = GYAxios.post("/reports/summary_groups", {
			...param,
			group: "MK2",
		});
		const CPALLSitesReq = GYAxios.post("/reports/summary_groups", {
			...param,
			group: "CP",
		});
		Axios.all([MK1SitesReq, MK2SitesReq, CPALLSitesReq]).then(
			Axios.spread((...responses) => {
				const formattedRes = responses.map((res) => res.data);
				let disabledArr = [];

				formattedRes.map(
					(group) => !group.isApproved && disabledArr.push(group.name)
				);

				setIsDisabled(disabledArr);
			})
		);
	}, [selectedMonth]);

	useEffect(() => {
		if (siteSet != "") {
			setIsReady(false);
			fetchData(siteSet);
			const newYearMonth = selectedMonth.format("MMMM YYYY");
			const newYear = selectedMonth.format("YYYY");

			if (newYearMonth !== yearMonth) setYearMonth(yearMonth);
			if (newYear !== selectedYear) setSelectedYear(newYear);
		}
	}, [siteSet, selectedMonth]);

	useEffect(() => {
		formatSummSites(summarySites);
	}, [summarySites]);

	return (
		<>
			<div className="perf-table panel panel-default">
				<div className="panel-heading ">
					<Row style={{ marginBottom: "10px" }}>
						<Col md="12">
							<h2 className="panel-title" id="date">
								Summary table - O&M
							</h2>
						</Col>
					</Row>
					<Row>
						<Col md="5">
							<div className="month-selector">
								Choose another Month: &nbsp;&nbsp;
								<DatePicker
									minDate={localTimeToPickerTime(minDate)}
									maxDate={localTimeToPickerTime(maxDate)}
									selected={localTimeToPickerTime(selectedMonth.toDate())}
									onChange={(date) => {
										setSelectedMonth(moment(pickerTimeTolocalTime(date)));
									}}
									dateFormat="MMM yyyy"
									showMonthYearPicker
								/>
							</div>
						</Col>
						<Col md="7" style={{ textAlign: "right" }}>
							<div id="summary-btns">
								<button
									className={"btn btn-primary"}
									onClick={() => {
										if (siteSet != "ALL") {
											setSiteSet("ALL");
										}
									}}
								>
									All Countries
								</button>
								<button
									className={"btn btn-primary"}
									onClick={() => {
										if (siteSet != "TH") {
											setSiteSet("TH");
										}
									}}
								>
									Thailand
								</button>
								<button
									className={"btn btn-primary"}
									onClick={() => {
										if (siteSet != "VN") {
											setSiteSet("VN");
										}
									}}
								>
									Vietnam
								</button>
								<button
									className={"btn btn-primary"}
									onClick={() => {
										if (siteSet !== "PH") {
											setSiteSet("PH");
										}
									}}
								>Philippines</button>

								{isDisabled != null && (
									<>
										<button
											className={
												!isDisabled.includes("MK1")
													? "btn btn-primary"
													: "btn btn-primary disabled"
											}
											onClick={() => {
												if (siteSet != "MK1" && !isDisabled.includes("MK1")) {
													setSiteSet("MK1");
												}

												if (isDisabled.includes("MK1")) alertMsg();
											}}
										>
											Makro P1
										</button>
										<button
											className={
												!isDisabled.includes("MK2")
													? "btn btn-primary"
													: "btn btn-primary disabled"
											}
											onClick={() => {
												if (siteSet != "MK2" && !isDisabled.includes("MK2")) {
													setSiteSet("MK2");
												}

												if (isDisabled.includes("MK2")) alertMsg();
											}}
										>
											Makro P2
										</button>
										<button
											className={
												!isDisabled.includes("CP")
													? "btn btn-primary"
													: "btn btn-primary disabled"
											}
											onClick={() => {
												if (siteSet != "CP" && !isDisabled.includes("CP")) {
													setSiteSet("CP");
												}

												if (isDisabled.includes("CP")) alertMsg();
											}}
										>
											CP All
										</button>
									</>
								)}
							</div>
						</Col>
					</Row>
				</div>
				<div className="panel-body">
					<MaterialTable
						columns={columns}
						data={rows}
						isLoading={!isReady}
						options={{
							showTitle: false,
							exportButton: true,
							exportAllData: true,
							grouping: true,
							pageSize: 10,
							pageSizeOptions: [10, 20, 40, 60],
							headerStyle: {
								fontSize: "1rem",
								textAlign: "center",
							},
							cellStyle: {
								textAlign: "center",
							},
							exportFileName: `SummRepOps-${yearMonth}-${siteSet}`,
						}}
					/>
				</div>
			</div>
			<Noti />
		</>
	);
};
